import { closestCalc } from "#src/util/helpers.ts";
import { BENEFIT_PERIOD_OPTIONS } from "#src/types/products/LinkedBenefit.ts";
import { type Mode, MODES } from "#src/constants/mode.ts";

import { type ProductType, PRODUCT_TYPE } from "#src/constants/products.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";

import {
  MONEY_POOL_OPTIONS,
  MONTHLY_BENEFITS,
  ELIMINATION_PERIODS,
  PARTNER_DISCOUNT,
  DISCOUNT_OPTIONS,
  MONTHLY_BENEFIT_MOST_COMMON,
  PARTNER_DISCOUNT_REQUIREMENTS,
  INFLATION_OPTION,
  RETURN_OF_PREMIUM_OPTION,
  DISCOUNT_OPTION,
  insuredQuoteData,
  getInflationDataItem,
  parseType,
  parseBoolean,
  type DiscountDataItem,
  type InflationDataItem,
  type ParamSuggestion,
  type PartnerDiscountDataItem,
  type PayDurationDataItem,
  type QuoteRequestParams,
  type ReturnOfPremiumDataItem,
  type InflationOption,
  type RenderRules,
  type ModeDataItem,
  type QuoteScenarioData,
  type PartnerDiscount,
  type ReturnOfPremiumOption,
  type DiscountOption,
  type ParsingData,
} from "#src/types/QuoteParams.ts";

export interface LongTermCareProductType extends ProductBase {
  isLTC: boolean;
  eliminationPeriodItems: number[];
  returnOfPremiumOptions: ReturnOfPremiumDataItem[];
  inflationOptions: InflationDataItem[];
  payDurationOptions: PayDurationDataItem[];
  partnerDiscountOptions: PartnerDiscountDataItem[];
  discountOptions: DiscountDataItem[];
  giveParamsSuggestion: ParamSuggestion;
  toQuoteScenario: (args: QuoteScenarioData) => Partial<QuoteRequestParams>;
  eAppParser(
    args: ParsingData
  ): LongTermCareProductType["defaults"] & { mode: Mode };

  defaults: {
    monthly_benefit: number;
    pool_of_money: number;
    benefit_period: number;
    elimination_period: number;
    pay_duration: number;
    home_health_care_waiver: boolean;
    use_pool_of_money: boolean;
    shared_care: boolean;
    joint_waiver_of_premium: boolean;
    inflation_percentage: InflationOption;
    partner_discount: PartnerDiscount;
    return_of_premium_rider: ReturnOfPremiumOption;
    discount: DiscountOption;
    save_age: boolean;
  };
}

function showHealth(conversion: boolean) {
  return !conversion;
}
export function LongTermCare(): LongTermCareProductType {
  return {
    get display(): string {
      return "Long Term Care";
    },
    get type(): ProductType {
      return PRODUCT_TYPE.LTC;
    },
    get isLTC(): boolean {
      return true;
    },
    get defaults() {
      return {
        monthly_benefit: MONTHLY_BENEFIT_MOST_COMMON,
        pool_of_money: 200000,
        benefit_period: 4,
        elimination_period: 90,
        pay_duration: -1,
        home_health_care_waiver: false,
        use_pool_of_money: false,
        shared_care: false,
        joint_waiver_of_premium: false,
        inflation_percentage: INFLATION_OPTION.COMPOUND_3,
        partner_discount: PARTNER_DISCOUNT.SINGLE,
        return_of_premium_rider: RETURN_OF_PREMIUM_OPTION.NONE,
        discount: DISCOUNT_OPTION.NONE,
        save_age: false,
      };
    },
    get requestCategory(): string {
      return "ltc";
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    get eliminationPeriodItems(): number[] {
      return ELIMINATION_PERIODS;
    },
    get returnOfPremiumOptions(): ReturnOfPremiumDataItem[] {
      return [
        {
          value: null,
          text: "None",
          subtitle: "You cannot get your LTC insurance premiums back.",
        },
        {
          value: "Limited",
          text: "Limited",
          subtitle:
            "Returns the total premiums paid, reduced by claims, to a beneficiary at death of the insured(s).",
        },
        {
          value: "Limited Surrender",
          text: "Limited Surrender",
          subtitle:
            "Limited plus up to 80% of the total premiums paid to the policyowner, reduced by claims if the policy is surrendered.",
        },
        {
          value: "Full",
          text: "Full",
          subtitle:
            "Returns the total premiums paid to a beneficiary at death of the insured(s).",
        },
        {
          value: "Full Surrender",
          text: "Full Surrender",
          subtitle:
            "Full plus up to 80% of the total premiums paid to the policyowner, reduced by claims if the policy is surrendered.",
        },
      ];
    },
    get inflationOptions(): InflationDataItem[] {
      const items: InflationOption[] = [
        INFLATION_OPTION.NONE,
        INFLATION_OPTION.COMPOUND_1,
        INFLATION_OPTION.COMPOUND_2,
        INFLATION_OPTION.COMPOUND_3,
        INFLATION_OPTION.COMPOUND_4,
        INFLATION_OPTION.COMPOUND_5,
      ];

      return items.map(getInflationDataItem);
    },
    get payDurationOptions(): PayDurationDataItem[] {
      return [
        {
          value: 1,
          title: "Single Pay",
        },
        {
          value: 10,
          title: "10 Pay",
        },
        {
          value: -1,
          title: "Lifetime Pay",
        },
      ];
    },
    get partnerDiscountOptions(): PartnerDiscountDataItem[] {
      return [
        {
          text: "Single",
          value: PARTNER_DISCOUNT.SINGLE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
        {
          text: "Married, Applying with Spouse",
          value: PARTNER_DISCOUNT.JOINT,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.VERBOSE,
        },
        {
          text: "Married, Applying Individually",
          value: PARTNER_DISCOUNT.SPOUSE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.BASIC,
        },
      ];
    },
    get discountOptions(): DiscountDataItem[] {
      return DISCOUNT_OPTIONS.map(({ text, value }) => ({
        title: text,
        value,
      }));
    },
    renderRules(params, options = { conversion: false }): Partial<RenderRules> {
      const sharedCare = params.partner_discount === PARTNER_DISCOUNT.JOINT;
      const jointPremiumWaiver =
        params.partner_discount === PARTNER_DISCOUNT.JOINT;

      const shouldShowHealth = showHealth(options.conversion);
      const jointInsuredHealth =
        params.partner_discount === PARTNER_DISCOUNT.JOINT && shouldShowHealth;

      const showPoolOfMoney = params.use_pool_of_money === true;

      return {
        saveAge: true,
        monthlyBenefit: true,
        benefitPeriod: !showPoolOfMoney,
        moneyPool: showPoolOfMoney,
        eliminationPeriod: true,
        homeHealthCareWaiver: true,
        returnOfPremium: true,
        payDuration: true,
        sharedCare: sharedCare,
        jointPremiumWaiver: jointPremiumWaiver,
        inflationPercentage: true,
        partnerDiscount: true,
        insuredHealth: shouldShowHealth,
        jointInsuredHealth: jointInsuredHealth,
        discount: true,
        mode: true,
        state: true,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      return {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        mode: args.params.mode,
        ...this.toQuoteScenario(args),
      };
    },
    toQuoteScenario({ params, otherInsured }): Partial<QuoteRequestParams> {
      let pool_of_money = params.pool_of_money;
      let benefit_period = params.benefit_period;
      if ([pool_of_money, benefit_period].includes(-1)) {
        pool_of_money = -1;
        benefit_period = -1;
      }
      const body: Partial<QuoteRequestParams> = {
        categories: [this.requestCategory],
        save_age: params.save_age,

        monthly_benefit: params.monthly_benefit,
        pool_of_money,
        benefit_period,
        use_pool_of_money: params.use_pool_of_money,
        elimination_period: params.elimination_period,
        home_health_care_waiver: params.home_health_care_waiver,
        shared_care: params.shared_care,
        joint_waiver_of_premium: params.joint_waiver_of_premium,
        inflation_percentage: params.inflation_percentage,
        pay_duration: params.pay_duration,
        return_of_premium_rider: params.return_of_premium_rider,
        discount: params.discount,
        partner_discount: params.partner_discount,
      };

      if (params.partner_discount === PARTNER_DISCOUNT.JOINT && otherInsured) {
        body.joint_party = insuredQuoteData(otherInsured);
      }

      return body;
    },
    eAppParser(model) {
      const parsedData: ReturnType<LongTermCareProductType["eAppParser"]> = {
        monthly_benefit: closestCalc(
          model.monthly_benefit || this.defaults.monthly_benefit,
          MONTHLY_BENEFITS
        ),
        partner_discount: parseType<PartnerDiscount>(
          model.partner_discount,
          this.partnerDiscountOptions.map(v => v.value),
          this.defaults.partner_discount
        ),
        inflation_percentage: parseType<InflationOption>(
          model.inflation_percentage,
          this.inflationOptions.map(v => v.value),
          this.defaults.inflation_percentage
        ),
        home_health_care_waiver: parseBoolean(
          model?.home_health_care_waiver,
          this.defaults.home_health_care_waiver
        ),
        elimination_period: parseType<number>(
          model.elimination_period,
          ELIMINATION_PERIODS,
          this.defaults.elimination_period
        ),
        shared_care: parseBoolean(
          model?.shared_care,
          this.defaults.shared_care
        ),
        pay_duration: parseType<number>(
          model?.pay_duration,
          this.payDurationOptions.map(v => v.value),
          this.defaults.pay_duration
        ),
        return_of_premium_rider: parseType<ReturnOfPremiumOption>(
          model.return_of_premium_rider,
          this.returnOfPremiumOptions.map(v => v.value),
          this.defaults.return_of_premium_rider
        ),
        discount: parseType<DiscountOption>(
          model.discount,
          DISCOUNT_OPTIONS.map(v => v.value),
          this.defaults.discount
        ),
        save_age: parseBoolean(model.save_age, this.defaults.save_age),
        benefit_period: parseType<number>(
          model.benefit_period,
          BENEFIT_PERIOD_OPTIONS.map(v => v.value),
          this.defaults.benefit_period
        ),
        pool_of_money: closestCalc(
          model.pool_of_money || this.defaults.pool_of_money,
          MONEY_POOL_OPTIONS
        ),
        joint_waiver_of_premium: parseBoolean(
          model.joint_waiver_of_premium,
          this.defaults.joint_waiver_of_premium
        ),
        use_pool_of_money: parseBoolean(
          model?.use_pool_of_money,
          this.defaults.use_pool_of_money
        ),
        mode: model.mode as Mode,
      };

      if ([parsedData.pool_of_money, parsedData.benefit_period].includes(-1)) {
        parsedData.pool_of_money = -1;
        parsedData.benefit_period = -1;
      }

      return parsedData;
    },
    giveParamsSuggestion(age, params): string {
      if (age < 30) {
        return "The minimum age for Long Term Care Insurance is 30";
      } else if (age > 79) {
        return "The maximum age for Long Term Care Insurance is 79";
      } else if (params.monthly_benefit && params.monthly_benefit < 3000) {
        return "Consider increasing your monthly benefit to $3,000";
      }
      return "";
    },
  };
}
